import React from "react";

/**
 * Renders a SENTINEL-tagged search fragment ("…la [[hl]]conjoncture[[/hl]] économique…")
 * as React nodes where each sentinel pair becomes a <mark>.
 *
 * Security contract: the fragment is PLAIN TEXT from the search engine — it is
 * never parsed as HTML. The output is built exclusively from string children
 * and <mark> elements, so any markup smuggled inside the fragment (e.g.
 * "<script>") renders as literal text. Unbalanced/lone sentinels degrade to
 * plain unmarked text of the same content.
 */
export const HIGHLIGHT_PRE = "[[hl]]";
export const HIGHLIGHT_POST = "[[/hl]]";

const SENTINEL_SPLIT = /\[\[hl\]\]|\[\[\/hl\]\]/;

/** True when the fragment carries at least one complete sentinel pair. */
export function hasSentinelHighlight(fragment: string | null | undefined): boolean {
  if (!fragment) return false;
  const pre = fragment.indexOf(HIGHLIGHT_PRE);
  return pre !== -1 && fragment.indexOf(HIGHLIGHT_POST, pre + HIGHLIGHT_PRE.length) !== -1;
}

/** The fragment with all sentinels stripped (for title/aria text). */
export function stripSentinels(fragment: string): string {
  return fragment.split(SENTINEL_SPLIT).join("");
}

export function renderSentinelHighlight(
  fragment: string,
  // Same visual as the search page's client-side highlight() marks.
  markClassName = "rounded-sm bg-[#FFF4B8] px-0.5 text-[#0F2849]"
): React.ReactNode {
  if (!hasSentinelHighlight(fragment)) {
    return stripSentinels(fragment);
  }
  // Split alternates: [outside, inside, outside, inside, …] because the source
  // engine always emits balanced, non-nested pairs. If the tail ends up
  // unbalanced (truncated fragment), the last odd chunk renders unmarked.
  const parts = fragment.split(SENTINEL_SPLIT);
  const nodes: React.ReactNode[] = [];
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (part.length === 0) continue;
    if (i % 2 === 1) {
      nodes.push(
        <mark key={i} className={markClassName}>
          {part}
        </mark>
      );
    } else {
      nodes.push(part);
    }
  }
  return nodes;
}
